import React, { useEffect, useRef, useState } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Upload, FileText, Trash2, Download } from "lucide-react";
import { formatDateTimeFr } from "@/lib/format";
import { toast } from "sonner";

export default function CompanyDocumentsTab({ company }) {
  const [docs, setDocs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const inputRef = useRef();

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/documents?resource_type=company&resource_id=${company.id}`);
      setDocs(data.items || []);
    } catch {} setLoading(false);
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [company.id]);

  const upload = async (e) => {
    const file = e.target.files?.[0]; if (!file) return;
    setUploading(true);
    const fd = new FormData();
    fd.append("file", file);
    try {
      await api.post(
        `/documents/upload?resource_type=company&resource_id=${company.id}`,
        fd,
        { headers: { "Content-Type": "multipart/form-data" } },
      );
      toast.success("Document ajouté"); load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Envoi impossible");
    }
    setUploading(false); e.target.value = "";
  };

  const del = async (id) => {
    if (!window.confirm("Supprimer ce document ?")) return;
    try { await api.delete(`/documents/${id}`); toast.success("Supprimé"); load(); }
    catch { toast.error("Suppression impossible"); }
  };

  const download = async (d) => {
    try {
      const resp = await api.get(`/documents/${d.id}/download`, { responseType: "blob" });
      const url = URL.createObjectURL(resp.data);
      const a = document.createElement("a"); a.href = url; a.download = d.original_filename; a.click();
      URL.revokeObjectURL(url);
    } catch { toast.error("Téléchargement impossible"); }
  };

  const CATEGORIES = [
    "Statuts", "Kbis", "Procès-verbal", "Pacte", "Document bancaire",
    "Déclaration fiscale", "Attestation", "Contrat", "Autre",
  ];

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <div className="text-overline">Coffre-fort</div>
          <h3 className="mt-1 font-heading text-xl">Documents de {company.denomination}</h3>
          <p className="text-sm text-muted-foreground mt-1">Statuts, Kbis, PV, pactes… Rangement par société.</p>
        </div>
        <div>
          <input ref={inputRef} type="file" className="hidden" onChange={upload}
                 accept=".pdf,.png,.jpg,.jpeg,.webp,.docx,.xlsx,.csv,.txt" />
          <Button className="btn-pill" onClick={() => inputRef.current?.click()} disabled={uploading} data-testid="company-doc-upload">
            <Upload className="mr-2 h-4 w-4" /> {uploading ? "Envoi…" : "Ajouter un document"}
          </Button>
        </div>
      </div>

      <div className="flex flex-wrap gap-2">
        {CATEGORIES.map((c) => (
          <span key={c} className="rounded-full border border-border bg-secondary/50 px-3 py-1 text-xs text-muted-foreground">
            {c}
          </span>
        ))}
      </div>

      <div className="card-elevated overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-secondary/40">
            <tr className="text-left text-xs uppercase tracking-wider text-muted-foreground">
              <th className="px-6 py-4">Nom</th>
              <th className="px-6 py-4">Type</th>
              <th className="px-6 py-4">Taille</th>
              <th className="px-6 py-4">Ajouté le</th>
              <th className="px-6 py-4"></th>
            </tr>
          </thead>
          <tbody className="divide-y divide-border">
            {loading && <tr><td colSpan={5} className="px-6 py-8 text-center text-muted-foreground">Chargement…</td></tr>}
            {!loading && docs.length === 0 && (
              <tr><td colSpan={5} className="px-6 py-12 text-center text-muted-foreground">
                <FileText className="mx-auto h-6 w-6 mb-2 text-muted-foreground/60" /> Aucun document.
              </td></tr>
            )}
            {docs.map((d) => (
              <tr key={d.id} className="hover:bg-secondary/30">
                <td className="px-6 py-3 flex items-center gap-2 font-medium">
                  <FileText className="h-4 w-4 text-muted-foreground" />
                  <span className="truncate max-w-[320px]">{d.original_filename}</span>
                </td>
                <td className="px-6 py-3 text-xs font-mono text-muted-foreground">{d.content_type}</td>
                <td className="px-6 py-3 text-muted-foreground">{Math.round((d.size || 0) / 1024)} Ko</td>
                <td className="px-6 py-3 text-muted-foreground text-xs">{formatDateTimeFr(d.created_at)}</td>
                <td className="px-6 py-3 text-right space-x-1">
                  <Button variant="ghost" size="sm" onClick={() => download(d)}><Download className="h-4 w-4" /></Button>
                  <Button variant="ghost" size="sm" onClick={() => del(d.id)}><Trash2 className="h-4 w-4 text-destructive" /></Button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
